import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faArrowLeft, faReceipt } from '@fortawesome/free-solid-svg-icons';
import { ApiService } from '../../../../core/services/api/api.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { ProductApiService } from '../../../product/services/api/product-api.service';
import { environment } from '../../../../../environments/environment';

interface PlatformListing {
  channel: string;
  channel_name: string;
  platform: string;
  sellable: number;
  platform_product_id: string;
  platform_sku: string;
}

interface ProductRow {
  sku: string;
  product_name: string;
  qty: number | string;
  sabuysoft_stock: number | null;
  stocks: PlatformListing[];
  stockMismatch: boolean;
  mismatchPlatform: string | null;
}

@Component({
  selector: 'app-branch-receipt-detail',
  templateUrl: './branch-receipt-detail.component.html',
  styleUrl: './branch-receipt-detail.component.css'
})
export class BranchReceiptDetailComponent implements OnInit {
  faReceipt = faReceipt;
  faArrowLeft = faArrowLeft;

  orderNo: string = '';
  orderDate: string = '';
  branchName: string = '';
  branchNumber: string = '';
  channelSelected: string = '';

  channels: any[] = [];
  channelsHeaders: string[] = [];

  loading: boolean = false;
  rows: ProductRow[] = [];
  errorMessage: string = '';

  packDate: string = '';
  receiveDate: string = '';
  hasDetail: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private platformService: PlatformService,
    private utilService: UtilService,
    private productApiService: ProductApiService,
  ) { }

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParamMap;
    this.orderNo = qp.get('order_no') || '';
    this.channelSelected = qp.get('channel') || '';
    this.orderDate = qp.get('order_date') || '';

    this.channels = (this.platformService?.channels || []).filter(c => !!c.is_branch);
    this.channelsHeaders = [...new Set(this.channels.map(c => c.platform))];
    const branch = this.channels.find(c => c.shop_ref === this.channelSelected);
    this.branchName = branch?.platform_name || '';
    this.branchNumber = branch?.branch_number || '';

    if (!this.orderNo || !this.branchNumber) {
      this.errorMessage = 'ข้อมูลออเดอร์ไม่ครบ';
      return;
    }
    this.fetchAll();
  }

  async fetchAll() {
    this.loading = true;
    this.errorMessage = '';
    this.rows = [];
    try {
      const productUrl = `${environment.endpoint}/receipt/get/product`;
      const detailUrl = `${environment.endpoint}/receipt/get/detail`;
      const body = { order_no: this.orderNo, branch: this.branchNumber };
      const [productRes, detailRes]: any[] = await Promise.all([
        this.apiService.postCall(productUrl, body),
        this.apiService.postCall(detailUrl, { order_no: [this.orderNo], branch: this.branchNumber }),
      ]);
      this.rows = (productRes?.data || []).map((r: any) => ({
        sku: r?.sku ?? '',
        product_name: r?.product_name ?? '',
        qty: r?.qty ?? 0,
        sabuysoft_stock: null,
        stocks: [],
        stockMismatch: false,
        mismatchPlatform: null,
      }));
      const detail = (detailRes?.data || []).find((d: any) => d?.order_no === this.orderNo);
      this.hasDetail = !!detail;
      this.packDate = this.formatDate(detail?.pack_date);
      this.receiveDate = this.formatDate(detail?.receive_date);
      await Promise.all([this.fetchSabuysoftStock(), this.fetchBranchStock()]);
      this.computeMismatches();
    } catch (e: any) {
      this.errorMessage = e?.message || 'เกิดข้อผิดพลาดในการเรียก API';
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  private async fetchSabuysoftStock() {
    const skus = this.rows.map(r => r.sku).filter(Boolean);
    if (skus.length === 0) return;
    try {
      const res: any = await this.productApiService.findStockSabuysoft({
        sku: skus.join(),
        branch: this.branchNumber,
      });
      const map = new Map<string, number>(
        (res?.data || []).map((e: any) => [e.sku, e.summary])
      );
      this.rows = this.rows.map(r => ({
        ...r,
        sabuysoft_stock: map.has(r.sku) ? Number(map.get(r.sku)) : null,
      }));
    } catch {
      // leave sabuysoft_stock null
    }
  }

  private async fetchBranchStock() {
    const skus = this.rows.map(r => r.sku).filter(Boolean);
    if (skus.length === 0 || this.channels.length === 0) return;
    try {
      const res: any = await this.productApiService.getAllProductPlatform({
        sku: skus.join(),
        channel: this.channels.map(c => c.shop_ref).join(),
        fields: 'sku,channel,sellable,platform_product_id,platform_sku',
      });
      const data = res?.data || res || [];
      const map = new Map<string, PlatformListing[]>();
      for (const e of (Array.isArray(data) ? data : [])) {
        const ch = this.channels.find(c => c.shop_ref === e.channel);
        if (!ch) continue;
        const listings = map.get(e.sku) || [];
        listings.push({
          channel: e.channel,
          channel_name: ch.platform_name,
          platform: ch.platform,
          sellable: Number(e.sellable ?? 0),
          platform_product_id: e?.platform_product_id ? String(e.platform_product_id) : '',
          platform_sku: e?.platform_sku ? String(e.platform_sku) : '',
        });
        map.set(e.sku, listings);
      }
      this.rows = this.rows.map(r => ({
        ...r,
        stocks: map.get(r.sku) || [],
      }));
    } catch {
      // leave stocks empty
    }
  }

  private computeMismatches() {
    if (!this.channelSelected) return;
    this.rows = this.rows.map(r => {
      let stockMismatch = false;
      let mismatchPlatform: string | null = null;
      if (r.sabuysoft_stock !== null) {
        const selectedEntry = r.stocks.find(s => s.channel === this.channelSelected);
        if (selectedEntry && Number(selectedEntry.sellable) !== Number(r.sabuysoft_stock)) {
          stockMismatch = true;
          mismatchPlatform = selectedEntry.platform;
        }
      }
      return { ...r, stockMismatch, mismatchPlatform };
    });
  }

  getStockByPlatform(platform: string, stocks: PlatformListing[]) {
    return stocks.filter(item => item.platform === platform);
  }

  getTooltipText(product: PlatformListing): string {
    return `ID: ${product.platform_product_id}\nSKU: ${product.platform_sku}`;
  }

  private formatDate(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const d = typeof value === 'number'
      ? this.utilService.dayjs.unix(value > 1e12 ? Math.floor(value / 1000) : value)
      : this.utilService.dayjs(value);
    return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : String(value);
  }

  back() {
    this.router.navigate(['/branch/receipt'], {
      queryParams: this.channelSelected ? { channel: this.channelSelected } : undefined,
    });
  }

  get totalQty(): number {
    return this.rows.reduce((sum, r) => sum + Number(r.qty || 0), 0);
  }
}
